<template>
	<div :class="[
		'min-h-screen flex flex-col overflow-x-hidden transition-colors duration-300 font-[\'Inter\',sans-serif]',
		isLight ? 'bg-[#e4f4f0]' : 'bg-[#0f1f1c]',
	]">
		<NavBarComponent :isLight="isLight" />

		<main class="flex-grow pt-20 relative overflow-hidden">
			<!-- Imigongo background patterns -->
			<div class="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
				<!-- top-right diamond grid -->
				<svg class="absolute -bottom-10 left-0 w-[240px] h-[240px] sm:w-[320px] sm:h-[320px] lg:w-[420px] lg:h-[420px]"
					:style="{ opacity: isLight ? 0.07 : 0.06 }" viewBox="0 0 160 160" fill="none"
					xmlns="http://www.w3.org/2000/svg">
					<g :fill="isLight ? '#0d6b5e' : '#a8d5cc'">
						<rect x="40" y="0" width="28" height="28" transform="rotate(45 40  0)" />
						<rect x="80" y="0" width="28" height="28" transform="rotate(45 80  0)" />
						<rect x="120" y="0" width="28" height="28" transform="rotate(45 120 0)" />
						<rect x="20" y="40" width="28" height="28" transform="rotate(45 20  40)" />
						<rect x="60" y="40" width="28" height="28" transform="rotate(45 60  40)" />
						<rect x="100" y="40" width="28" height="28" transform="rotate(45 100 40)" />
						<rect x="140" y="40" width="28" height="28" transform="rotate(45 140 40)" />
						<rect x="40" y="80" width="28" height="28" transform="rotate(45 40  80)" />
						<rect x="80" y="80" width="28" height="28" transform="rotate(45 80  80)" />
						<rect x="120" y="80" width="28" height="28" transform="rotate(45 120 80)" />
						<rect x="20" y="120" width="20" height="20" transform="rotate(45 20  120)" />
						<rect x="60" y="120" width="20" height="20" transform="rotate(45 60  120)" />
						<rect x="100" y="120" width="20" height="20" transform="rotate(45 100 120)" />
						<rect x="140" y="120" width="20" height="20" transform="rotate(45 140 120)" />
					</g>
				</svg>
				<!-- bottom-left concentric squares -->
				<svg class="absolute -bottom-10 left-0 w-[180px] h-[180px] sm:w-[240px] sm:h-[240px] lg:w-[280px] lg:h-[280px]"
					:style="{ opacity: isLight ? 0.06 : 0.05 }" viewBox="0 0 120 120" fill="none"
					xmlns="http://www.w3.org/2000/svg">
					<g :stroke="isLight ? '#0f1f1c' : '#a8d5cc'" stroke-width="3" fill="none">
						<rect x="60" y="10" width="64" height="64" transform="rotate(45 60 10)" />
						<rect x="60" y="22" width="46" height="46" transform="rotate(45 60 22)" />
						<rect x="60" y="34" width="28" height="28" transform="rotate(45 60 34)" />
						<rect x="60" y="46" width="10" height="10" transform="rotate(45 60 46)" />
					</g>
				</svg>
				<!-- mid-left small diamonds -->
				<svg class="hidden sm:block absolute top-1/2 -left-8 w-[180px] h-[180px] -translate-y-1/2"
					:style="{ opacity: isLight ? 0.05 : 0.04 }" viewBox="0 0 80 80" fill="none"
					xmlns="http://www.w3.org/2000/svg">
					<g :fill="isLight ? '#0d6b5e' : '#a8d5cc'">
						<rect x="20" y="0" width="16" height="16" transform="rotate(45 20 0)" />
						<rect x="50" y="0" width="16" height="16" transform="rotate(45 50 0)" />
						<rect x="5" y="30" width="16" height="16" transform="rotate(45 5  30)" />
						<rect x="35" y="30" width="16" height="16" transform="rotate(45 35 30)" />
						<rect x="65" y="30" width="16" height="16" transform="rotate(45 65 30)" />
						<rect x="20" y="60" width="16" height="16" transform="rotate(45 20 60)" />
						<rect x="50" y="60" width="16" height="16" transform="rotate(45 50 60)" />
					</g>
				</svg>
			</div>

			<!-- Loading -->
			<div v-if="loading" class="flex flex-col items-center justify-center py-40 gap-5">
				<div class="w-10 h-10 rounded-full border-2 border-t-transparent animate-spin"
					:class="isLight ? 'border-[#1a9b8a]' : 'border-[#a8d5cc]'"></div>
				<p class="text-[11px] font-semibold tracking-widest uppercase"
					:class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'">
					{{ $t("eventDetail.loading") }}
				</p>
			</div>

			<!-- Not found -->
			<div v-else-if="!event" class="flex flex-col items-center justify-center py-40 gap-5">
				<svg class="w-14 h-14" :style="{ opacity: isLight ? 0.25 : 0.2 }" viewBox="0 0 64 64" fill="none">
					<rect x="32" y="2" width="38" height="38" transform="rotate(45 32 2)"
						:fill="isLight ? '#0d6b5e' : '#a8d5cc'" />
					<rect x="32" y="14" width="22" height="22" transform="rotate(45 32 14)"
						:fill="isLight ? '#e4f4f0' : '#0f1f1c'" />
					<rect x="32" y="24" width="8" height="8" transform="rotate(45 32 24)"
						:fill="isLight ? '#0d6b5e' : '#a8d5cc'" />
				</svg>
				<p class="font-['Montserrat'] font-extrabold text-2xl tracking-tight"
					:class="isLight ? 'text-[#0f1f1c]' : 'text-white'">
					{{ $t("eventDetail.notFound") }}
				</p>
			</div>

			<!-- Content -->
			<div v-else class="max-w-6xl mx-auto px-3 sm:px-4 md:px-6 pb-16 sm:pb-20">
				<div class="grid grid-cols-1 lg:grid-cols-2 gap-5 md:gap-8">
					<!-- ══════════════════ LEFT COLUMN ══════════════════ -->
					<div class="space-y-5 p-2 min-w-0">
						<!-- Image -->
						<div class="relative rounded-2xl overflow-hidden group w-full min-w-0">
							<div v-if="eventStatus.status === 'ENDED'"
								class="absolute inset-0 bg-black/55 z-10 flex items-center justify-center backdrop-blur-[2px]">
								<span
									class="font-['Montserrat'] font-black text-white/40 text-3xl tracking-[.3em] uppercase">{{
										$t("eventDetail.ended") }}</span>
							</div>
							<div class="absolute top-3 right-3 z-20 g">
								<div v-if="eventStatus.status === 'LIVE'"
									class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-black tracking-widest uppercase bg-red-500 text-white shadow-lg shadow-red-500/30">
									<span class="w-1.5 h-1.5 rounded-full bg-white animate-pulse"></span>{{
										$t("eventDetail.liveNow") }}
								</div>
								<div v-else-if="eventStatus.status === 'STARTING SOON'"
									class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-bold tracking-widest uppercase border bg-amber-400/20 text-amber-300 border-amber-400/40 backdrop-blur-md">
									<span class="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse"></span>{{
										$t("eventDetail.startingSoon") }}
								</div>
								<div v-else-if="eventStatus.status === 'ENDED'"
									class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-bold tracking-widest uppercase border border-white/10 text-white/40 bg-black/30 backdrop-blur-md">
									{{ $t("eventDetail.ended") }}
								</div>
								<div v-else-if="eventStatus.status === 'RSVP'"
									class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-bold tracking-widest uppercase border bg-[#1a9b8a]/15 text-[#087f72] border-[#1a9b8a]/40 backdrop-blur-md mr-1">
									{{ $t("eventDetail.rsvp") }}
								</div>
								<!-- Approval required badge -->
								<div v-if="event.private"
									class="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[9px] font-bold tracking-widest uppercase border bg-yellow-400 text border-yellow-400/40 backdrop-blur-md shadow-lg shadow-yellow-500/10">
									{{ $t('eventDetail.approvalRequired') }}
								</div>
							</div>
							<button @click="$router.push('/')"
								class="absolute top-3 left-3 z-20 w-9 h-9 flex items-center justify-center rounded-full border backdrop-blur-md transition-all duration-200"
								:class="isLight
										? 'bg-white/80 border-[#1a9b8a]/30 text-[#0d6b5e] hover:bg-white'
										: 'bg-black/50 border-white/15 text-white/80 hover:bg-black/70'
									">
								<svg fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor"
									class="w-4 h-4">
									<path stroke-linecap="round" stroke-linejoin="round"
										d="M15.75 19.5L8.25 12l7.5-7.5" />
								</svg>
							</button>
							<img v-if="event.image_url" :src="event.image_url" :alt="event.title"
								class="w-full h-[240px] sm:h-[300px] md:h-64 lg:h-[420px] flex items-center justify-center object-cover" />
							<div v-else class="w-full h-64 flex items-center justify-center"
								:class="isLight ? 'bg-[#1a9b8a]/10' : 'bg-white/[0.03]'">
								<span
									class="font-['Montserrat'] font-black text-[40px] leading-none tracking-[-4px] select-none pointer-events-none text-center px-6"
									:class="isLight ? 'text-black/10' : 'text-white/[0.05]'">
									{{ event.title?.toUpperCase() }}
								</span>
							</div>
						</div>

						<!-- Title -->
						<h2 class="font-['Montserrat'] font-extrabold text-[26px] sm:text-3xl md:text-[34px] leading-tight tracking-tight text-left break-words"
							:class="isLight ? 'text-[#0f1f1c]' : 'text-white'">
							{{ event.title }}
						</h2>

						<!-- Date · Organizer slug · Logos row -->
						<div class="flex flex-col sm:flex-row sm:items-center gap-4">
							<!-- Date badge -->
							<div class="flex items-center gap-3 flex-shrink-0">
								<div class="rounded-xl overflow-hidden border text-center w-12 flex-shrink-0" :class="isLight ? 'border-[#1a9b8a]/25' : 'border-[#1a9b8a]/30'
									">
									<div
										class="text-[9px] font-black tracking-widest uppercase py-0.5 bg-[#1a9b8a] text-white">
										{{
											new Date(event.date).toLocaleString("en-GB", {
												month: "short",
											})
										}}
									</div>
									<div class="text-2xl font-black leading-none py-1.5"
										:class="isLight ? 'text-[#0f1f1c]' : 'text-white'">
										{{ new Date(event.date).getDate() }}
									</div>
								</div>
								<div class="flex-shrink-0">
									<p class="text-sm font-semibold" :class="isLight ? 'text-[#0f1f1c]' : 'text-white'">
										{{ formattedStartDateTime }}
									</p>
									<p class="text-xs mt-0.5" :class="isLight ? 'text-[#0f1f1c]/50' : 'text-white/40'">
										→ {{ formattedEndDateTime }}
									</p>
								</div>
							</div>

							<!-- Organizer slug pill + logos -->
							<div class="flex items-center gap-2 sm:ml-auto flex-wrap min-w-0 max-w-full">
								<button v-if="event.organizer?.[0]?.handle" @click="showOrganizerPopup = true"
									class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] max-w-full truncate font-semibold border flex-shrink-0 transition-all duration-200"
									:class="isLight
											? 'border-[#1a9b8a]/25 text-[#0d6b5e] bg-[#1a9b8a]/8 hover:bg-[#1a9b8a]/15'
											: 'border-[#1a9b8a]/25 text-[#a8d5cc] bg-[#1a9b8a]/8 hover:bg-[#1a9b8a]/15'
										">
									<svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
											d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
									</svg>
									@{{ event.organizer[0].handle }}
								</button>

								<div v-if="
									event.organizerLogos && Array.isArray(event.organizerLogos)
								" class="flex items-center overflow-x-auto scrollbar-none max-w-full sm:max-w-[120px]">
									<template v-for="(logo, index) in event.organizerLogos" :key="index">
										<img :src="logo.url || '/placeholder-logo.png'" alt="Organizer"
											class="h-8 w-8 rounded-full border-2 object-cover flex-shrink-0 transition-transform duration-200 hover:scale-110 -ml-2 first:ml-0"
											:class="isLight ? 'border-[#e4f4f0]' : 'border-[#0f1f1c]'
												" />
									</template>
								</div>
							</div>
						</div>

						<!-- Categories + share -->
						<div class="flex items-start sm:items-center gap-2">
							<div class="flex gap-2 overflow-x-auto scrollbar-none flex-1 min-w-0 max-w-full pb-1">
								<template v-for="(category, index) in event.categories" :key="'tag-' + index">
									<span
										class="px-3 py-1.5 rounded-full text-[11px] font-medium border flex-shrink-0 transition-colors duration-200"
										:class="isLight
												? 'bg-[#1a9b8a]/10 text-[#0d6b5e] border-[#1a9b8a]/25 hover:bg-[#1a9b8a]/20'
												: 'bg-[#1a9b8a]/10 text-[#a8d5cc] border-[#1a9b8a]/20 hover:bg-[#1a9b8a]/20'
											">
										#{{ category.name }}
									</span>
								</template>
							</div>
							<button
								@click="shareEvent"
								class="flex-shrink-0 flex h-9 w-9 items-center justify-center rounded-full border transition-all duration-200 shadow-sm"
								:class="isLight
									? 'border-gray-300  text-gray-700 hover:bg-[#1a9b8a] hover:border-[#1a9b8a] hover:text-white'
									: 'border-[#3b4f4b] bg-[#243633] text-[#d7ebe7] hover:bg-[#0d6b5e] shadow-lg shadow-[#1a9b8a]/20'
								">
								<svg
									class="h-4 w-4"
									fill="none"
									viewBox="0 0 24 24"
									stroke="currentColor">
									<path
										stroke-linecap="round"
										stroke-linejoin="round"
										stroke-width="2"
										d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
								</svg>
							</button>
						</div>

						<!-- About -->
						<div class="rounded-2xl border px-4 sm:px-6 py-4 sm:py-5" :class="isLight
								? 'bg-white/60 border-[#1a9b8a]/15'
								: 'bg-white/[0.03] border-white/[0.07]'
							">
							<p class="text-[11px] font-semibold tracking-widest uppercase mb-4"
								:class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'">
								{{ $t("eventDetail.aboutLabel") }}
							</p>
							<div class="text-sm leading-relaxed"
								:class="isLight ? 'text-[#0f1f1c]/70' : 'text-white/55'" v-html="event.description || $t('eventDetail.noDescription')
									"></div>
						</div>
					</div>

					<!-- ══════════════════ RIGHT COLUMN ══════════════════ -->
					<div class="space-y-5 p-2 min-w-0">
						<!-- Tickets -->
						<div v-if="isEventBookable">
							<p class="text-[11px] font-semibold tracking-widest uppercase mb-1"
								:class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'">
								{{ $t("eventDetail.chooseTicket") }}
							</p>
							<p class="text-xs mb-4" :class="isLight ? 'text-[#0f1f1c]/45' : 'text-white/35'">
								{{ $t("eventDetail.selectTicketHint") }}
							</p>

							<div class="space-y-2 mb-5">
								<div v-for="(ticket, index) in event.tickets" :key="index"
									class="ticket-option-container">
									<input type="radio" name="ticket" :id="'ticket' + index" class="hidden"
										v-model="selectedTicket" :value="ticket.id"
										:disabled="eventStatus.status === 'ENDED'" />
									<div :class="[
										'flex flex-col gap-3 sm:flex-row sm:items-center px-3 sm:px-4 py-3.5 rounded-xl transition-all duration-200 overflow-hidden',
										eventStatus.status === 'ENDED'
											? 'opacity-50 cursor-not-allowed pointer-events-none'
											: 'cursor-pointer',
										selectedTicket === ticket.id
											? isLight
												? 'bg-[#1a9b8a] shadow-md shadow-[#1a9b8a]/25'
												: 'bg-[#1a9b8a] shadow-md shadow-[#1a9b8a]/20'
											: isLight
												? 'bg-black/[0.04] hover:bg-black/[0.07]'
												: 'bg-white/[0.05] hover:bg-white/[0.08]',
									]" @click="
											eventStatus.status !== 'ENDED' && selectTicket(ticket)
											">
										<div class="w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all duration-200"
											:class="selectedTicket === ticket.id
													? 'border-white bg-white/20'
													: isLight
														? 'border-black/20'
														: 'border-white/25'
												">
											<div v-if="selectedTicket === ticket.id"
												class="w-1.5 h-1.5 rounded-full bg-white"></div>
										</div>
										<div class="flex-1 min-w-0 w-full overflow-hidden">
											<p class="text-sm font-semibold transition-colors duration-200" :class="selectedTicket === ticket.id
													? 'text-white'
													: isLight
														? 'text-[#0f1f1c]'
														: 'text-white'
												">
												{{ ticket.name }}
											</p>
											<p class="text-xs font-bold mt-0.5 transition-colors duration-200" :class="selectedTicket === ticket.id
													? 'text-white/75'
													: isLight
														? 'text-[#1a9b8a]'
														: 'text-[#a8d5cc]'
												">
												{{ formatCurrency(ticket.price) }}
											</p>
										</div>
										<div v-if="selectedTicket === ticket.id"
											class="flex items-center justify-between gap-2 w-full sm:w-auto sm:justify-start flex-shrink-0 min-w-0"
											@click.stop>
											<button @click="ticketQty = Math.max(1, ticketQty - 1)"
												:disabled="eventStatus.status === 'ENDED' || event.private"
												class="w-7 h-7 rounded-full bg-white/20 hover:bg-white/35 flex items-center justify-center text-white text-sm font-bold transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed">
												−
											</button>
											<span class="text-sm font-bold text-white w-4 text-center">{{ ticketQty
												}}</span>
											<button @click="ticketQty = Math.min(10, ticketQty + 1)"
												:disabled="eventStatus.status === 'ENDED' || event.private"
												class="w-7 h-7 rounded-full bg-white/20 hover:bg-white/35 flex items-center justify-center text-white text-sm font-bold transition-all duration-200 disabled:opacity-40 disabled:cursor-not-allowed">
												+
											</button>
										</div>
									</div>
								</div>
							</div>

							<div class="border-t mb-4" :class="isLight ? 'border-black/8' : 'border-white/[0.06]'
								"></div>

							<!-- Total -->
							<div v-if="selectedTicket && selectedTicketObj"
								class="flex items-center justify-between mb-4">
								<span class="text-xs" :class="isLight ? 'text-[#0f1f1c]/50' : 'text-white/40'">
									{{ ticketQty }} × {{ selectedTicketObj.name }}
								</span>
								<span class="text-sm font-bold" :class="isLight ? 'text-[#0f1f1c]' : 'text-white'">
									{{ formatCurrency(selectedTicketObj.price * ticketQty) }}
								</span>
							</div>

							<!-- Book CTA -->
							<button v-if="eventStatus.status !== 'ENDED'" type="button" @click="openBookingModal"
								class="w-full py-3.5 rounded-xl text-sm font-bold tracking-wide transition-all duration-200 bg-[#1a9b8a] text-white hover:bg-[#0d6b5e] shadow-lg shadow-[#1a9b8a]/20">
								{{ event.private ? $t("eventDetail.requestToAttend") : $t("eventDetail.bookNow") }}
								<span v-if="selectedTicket && selectedTicketObj" class="opacity-70 font-normal ml-1">
									· {{ formatCurrency(selectedTicketObj.price * ticketQty) }}
								</span>
							</button>
							<p v-if="event.private && eventStatus.status !== 'ENDED'" class="text-[11px] text-center mt-2 opacity-60"
								:class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'">
								{{ $t('eventDetail.approvalNote') }}
							</p>
							<div v-if="eventStatus.status === 'ENDED'"
								class="w-full min-h-[48px] py-3 rounded-xl text-sm font-medium text-center border"
								:class="isLight
										? 'border-black/10 text-[#0f1f1c]/40 bg-black/[0.03]'
										: 'border-white/[0.06] text-white/25 bg-white/[0.02]'
									">
								{{ $t("eventDetail.eventEnded") }}
							</div>

							<!-- Secondary actions -->
							<div v-if="eventStatus.status !== 'ENDED'" class="space-y-2 mt-2">
								<a :href="`https://www.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(
									event.title,
								)}&dates=${event.date
									.split('T')[0]
									.replace(/-/g, '')}&details=${encodeURIComponent(
										event.description || '',
									)}`" target="_blank" class="w-full min-h-[48px] py-3 rounded-xl text-sm font-semibold text-center border flex items-center justify-center gap-2 transition-all duration-200"
									:class="isLight
											? 'border-[#1a9b8a]/25 text-[#0d6b5e] hover:bg-[#1a9b8a]/10'
											: 'border-[#1a9b8a]/25 text-[#a8d5cc] hover:bg-[#1a9b8a]/10'
										">
									<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
											d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 012.25-2.25h13.5A2.25 2.25 0 0121 7.5v11.25m-18 0A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75m-18 0v-7.5A2.25 2.25 0 015.25 9h13.5A2.25 2.25 0 0121 11.25v7.5" />
									</svg>
									{{ $t("eventDetail.addToCalendar") }}
								</a>
							</div>
							<div class="space-y-2 mt-2">
								<button @click="$router.push('/discover')"
									class="w-full min-h-[48px] py-3 rounded-xl text-sm font-semibold flex items-center justify-center gap-2 transition-all duration-200"
									:class="isLight
											? 'bg-black/[0.04] text-[#0f1f1c]/60 hover:bg-black/[0.07]'
											: 'bg-white/[0.05] text-white/50 hover:bg-white/[0.09]'
										">
									<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5"
											d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 15.803 7.5 7.5 0 0016.803 15.803z" />
									</svg>
									{{ $t("eventDetail.discoverSimilar") }}
								</button>
							</div>

							<div v-if="enableRecommendMusicSection" class="mt-3 text-center">
								<button @click="openRecommendMusicModal"
									class="text-xs font-medium transition-colors duration-200" :class="isLight
											? 'text-[#1a9b8a] hover:text-[#0d6b5e]'
											: 'text-[#a8d5cc] hover:text-white'
										">
									{{ $t("eventDetail.recommendMusic") }}
								</button>
							</div>
						</div>

						<!-- Location + map -->
						<div class="rounded-2xl border overflow-hidden" :class="isLight
								? 'bg-white/60 border-[#1a9b8a]/15'
								: 'bg-white/[0.03] border-white/[0.07]'
							">
							<div class="px-5 pt-4 pb-2">
								<p class="text-[11px] font-semibold tracking-widest uppercase"
									:class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'">
									{{ $t("eventDetail.locationLabel") }}
								</p>
							</div>
							<div id="event-map" class="h-48 sm:h-52 md:h-64 w-full"></div>
							<a :href="`https://www.google.com/maps?q=${event.latitude},${event.longitude}`"
								target="_blank"
								class="flex items-center gap-2.5 px-5 py-3 transition-colors duration-200 group" :class="isLight
										? 'text-[#0f1f1c]/60 hover:text-[#1a9b8a]'
										: 'text-white/40 hover:text-[#a8d5cc]'
									">
								<div class="w-6 h-6 rounded-lg flex items-center justify-center flex-shrink-0" :class="isLight
										? 'bg-[#1a9b8a]/10 text-[#1a9b8a]'
										: 'bg-[#1a9b8a]/10 text-[#a8d5cc]'
									">
									<svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
											d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
											d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
									</svg>
								</div>
								<span class="text-xs truncate min-w-0">{{
									address || $t("eventDetail.viewOnMaps")
								}}</span>
								<svg class="w-3 h-3 ml-auto flex-shrink-0 opacity-0 group-hover:opacity-60 transition-opacity"
									fill="none" viewBox="0 0 24 24" stroke="currentColor">
									<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
										d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
								</svg>
							</a>
						</div>

						<!-- Event notes -->
						<div v-if="event.notes && event.notes.trim()" class="rounded-2xl border px-5 py-4" :class="isLight
								? 'bg-amber-50/60 border-amber-200/60'
								: 'bg-amber-400/[0.06] border-amber-400/15'
							">
							<div class="flex items-start gap-3">
								<div class="w-5 h-5 rounded-full flex-shrink-0 flex items-center justify-center mt-0.5"
									:class="isLight
											? 'bg-amber-400/20 text-amber-600'
											: 'bg-amber-400/15 text-amber-400'
										">
									<svg class="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
										<path fill-rule="evenodd"
											d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
											clip-rule="evenodd" />
									</svg>
								</div>
								<p class="text-xs leading-relaxed"
									:class="isLight ? 'text-amber-800/80' : 'text-amber-300/70'">
									{{ event.notes }}
								</p>
							</div>
						</div>

						<!-- Organizer card -->
						<div class="rounded-2xl border px-4 sm:px-5 py-4 sm:py-5" :class="isLight
								? 'bg-white/60 border-[#1a9b8a]/15'
								: 'bg-white/[0.03] border-white/[0.07]'
							">
							<p class="text-[11px] font-semibold tracking-widest uppercase mb-4"
								:class="isLight ? 'text-[#0d6b5e]' : 'text-[#a8d5cc]'">
								{{ $t("eventDetail.organizerLabel") }}
							</p>
							<div class="flex items-center gap-3 mb-4">
								<div class="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0"
									:class="isLight ? 'bg-[#1a9b8a]/15' : 'bg-[#1a9b8a]/10'">
									<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"
										class="w-6 h-6" :class="isLight ? 'text-[#1a9b8a]' : 'text-[#a8d5cc]'">
										<path fill-rule="evenodd"
											d="M7.5 6a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM3.751 20.105a8.25 8.25 0 0116.498 0 .75.75 0 01-.437.695A18.683 18.683 0 0112 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 01-.437-.695z"
											clip-rule="evenodd" />
									</svg>
								</div>
								<div>
									<p class="text-sm font-bold text-left"
										:class="isLight ? 'text-[#0f1f1c]' : 'text-white'">
										{{ event.organizer[0].Name }}
									</p>
									<p class="text-xs mt-0.5 text-left"
										:class="isLight ? 'text-[#0f1f1c]/50' : 'text-white/40'">
										{{ event.organizer[0].Email }}
									</p>
								</div>
							</div>

							<div class="flex items-center gap-2 flex-wrap min-w-0">
								<a v-if="event.organizer[0].handle"
									:href="`https://${event.organizer[0].handle}.trekko.xyz/`" target="_blank"
									class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] max-w-full truncate font-semibold border transition-all duration-200"
									:class="isLight
											? 'border-[#1a9b8a]/25 text-[#0d6b5e] bg-[#1a9b8a]/8 hover:bg-[#1a9b8a]/15'
											: 'border-[#1a9b8a]/25 text-[#a8d5cc] bg-[#1a9b8a]/8 hover:bg-[#1a9b8a]/15'
										">
									<svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
										<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
											d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
									</svg>
									<span class="truncate max-w-[140px] sm:max-w-none block">
										{{ event.organizer[0].handle }}.trekko.xyz
									</span>
								</a>

								<!-- X / Twitter -->
								<a v-if="event.organizer[0].twitter"
									:href="`https://x.com/${event.organizer[0].twitter}`" target="_blank"
									class="w-8 h-8 rounded-full border flex items-center justify-center transition-all duration-200"
									:class="isLight
											? 'border-black/10 text-[#0f1f1c]/50 hover:border-black/25 hover:text-[#0f1f1c]'
											: 'border-white/[0.08] text-white/35 hover:border-white/20 hover:text-white'
										">
									<svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
										<path
											d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
									</svg>
								</a>
								<button v-else
									class="w-8 h-8 rounded-full border flex items-center justify-center opacity-30 cursor-default"
									:class="isLight
											? 'border-black/10 text-[#0f1f1c]/50'
											: 'border-white/[0.08] text-white/35'
										">
									<svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
										<path
											d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
									</svg>
								</button>

								<!-- Instagram -->
								<a v-if="event.organizer[0].instagram"
									:href="`https://instagram.com/${event.organizer[0].instagram}`" target="_blank"
									class="w-8 h-8 rounded-full border flex items-center justify-center transition-all duration-200"
									:class="isLight
											? 'border-black/10 text-[#0f1f1c]/50 hover:border-pink-400/40 hover:text-pink-500'
											: 'border-white/[0.08] text-white/35 hover:border-pink-400/40 hover:text-pink-400'
										">
									<svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
										<path
											d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
									</svg>
								</a>
								<button v-else
									class="w-8 h-8 rounded-full border flex items-center justify-center opacity-30 cursor-default"
									:class="isLight
											? 'border-black/10 text-[#0f1f1c]/50'
											: 'border-white/[0.08] text-white/35'
										">
									<svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
										<path
											d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
									</svg>
								</button>
							</div>
						</div>
					</div>
				</div>
			</div>
		</main>

		<FooterComponent :isLight="isLight" @toggle-mode="toggleMode" />

		<!-- Organizer popup modal -->
		<transition name="fade">
			<div v-if="showOrganizerPopup" class="fixed inset-0 z-[9999] flex items-center justify-center px-4"
				@click.self="showOrganizerPopup = false">
				<div class="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
				<div class="relative rounded-2xl border w-full max-w-sm p-4 sm:p-6 z-10 shadow-2xl max-h-[90vh] overflow-y-auto"
					:class="isLight
							? 'bg-white border-[#1a9b8a]/20'
							: 'bg-[#132420] border-[#1a9b8a]/20'
						">
					<button @click="showOrganizerPopup = false"
						class="absolute top-4 right-4 w-7 h-7 rounded-full border flex items-center justify-center transition-all duration-200"
						:class="isLight
								? 'border-black/10 text-[#0f1f1c]/40 hover:text-[#0f1f1c]'
								: 'border-white/10 text-white/30 hover:text-white'
							">
						<svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
								d="M6 18L18 6M6 6l12 12" />
						</svg>
					</button>
					<div class="flex items-center gap-4 mb-5">
						<div class="w-14 h-14 rounded-full flex items-center justify-center flex-shrink-0"
							:class="isLight ? 'bg-[#1a9b8a]/15' : 'bg-[#1a9b8a]/10'">
							<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor"
								class="w-8 h-8" :class="isLight ? 'text-[#1a9b8a]' : 'text-[#a8d5cc]'">
								<path fill-rule="evenodd"
									d="M7.5 6a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM3.751 20.105a8.25 8.25 0 0116.498 0 .75.75 0 01-.437.695A18.683 18.683 0 0112 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 01-.437-.695z"
									clip-rule="evenodd" />
							</svg>
						</div>
						<div>
							<p class="font-['Montserrat'] font-bold text-lg"
								:class="isLight ? 'text-[#0f1f1c]' : 'text-white'">
								{{ event?.organizer[0].Name }}
							</p>
							<p class="text-[11px] mt-0.5 font-semibold"
								:class="isLight ? 'text-[#1a9b8a]' : 'text-[#a8d5cc]'">
								@{{ event?.organizer[0].handle }}
							</p>
						</div>
					</div>
					<p class="text-sm leading-relaxed mb-5" :class="isLight ? 'text-[#0f1f1c]/65' : 'text-white/50'">
						{{
							event?.organizer[0].bio ||
							"Event organizer on Trekko. Discover all their upcoming events on their profile page."
						}}
					</p>
					<div class="border-t mb-5" :class="isLight ? 'border-black/8' : 'border-white/[0.06]'"></div>
					<p class="text-xs mb-4" :class="isLight ? 'text-[#0f1f1c]/40' : 'text-white/30'">
						{{ event?.organizer[0].Email }}
					</p>
					<a v-if="event?.organizer[0].handle" :href="`https://${event.organizer[0].handle}.trekko.xyz/`"
						target="_blank"
						class="w-full min-h-[48px] py-3 rounded-xl text-sm font-bold flex items-center justify-center gap-2 transition-all duration-200 bg-[#1a9b8a] text-white hover:bg-[#0d6b5e] shadow-lg shadow-[#1a9b8a]/20">
						<svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
								d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
						</svg>
						{{ $t("eventDetail.visitPage") }}
					</a>
				</div>
			</div>
		</transition>

		<BookingModal v-if="isBookingModalOpen" :isOpen="isBookingModalOpen" @close="closeModal" :ticket="selectedTicketObj ? { ...selectedTicketObj, quantity: ticketQty } : null
			" :event="event" :isLight="isLight" :isMusicRemmandationEnabled="enableRecommendMusicSection" />
		<RecommendMusicModal v-if="isRecommendMusicModalOpen" :isOpen="isRecommendMusicModalOpen" @close="closeModal"
			:event="event" />
	</div>
</template>

<script>
import { useHead } from "@vueuse/head";
import apiService from "@/services/api";
import NavBarComponent from "@/components/NavBarComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import BookingModal from "@/components/BookingModal.vue";
import { useToast } from "vue-toastification";
import RecommendMusicModal from "@/components/RecommendMusicModal.vue";
import { useTheme } from "@/composables/useTheme";

export default {
	components: {
		NavBarComponent,
		FooterComponent,
		BookingModal,
		RecommendMusicModal,
	},

	setup() {
		const toast = useToast();
		const { isLight, toggleMode } = useTheme();
		return { toast, isLight, toggleMode };
	},

	data() {
		return {
			event: null,
			isEventBookable: false,
			loading: true,
			address: "",
			isBookingModalOpen: false,
			selectedTicketId: null,
			selectedTicket: null,
			ticketQty: 1,
			enableRecommendMusicSection: false,
			isRecommendMusicModalOpen: false,
			showOrganizerPopup: false,
		};
	},

	computed: {
		selectedTicketObj() {
			if (!this.selectedTicket || !this.event?.tickets) return null;
			return (
				this.event.tickets.find((t) => t.id === this.selectedTicket) || null
			);
		},

		formattedStartDateTime() {
			try {
				const startDateStr = `${this.event.date.split("T")[0]}T${this.event.start_time
					}:00Z`;
				const date = new Date(startDateStr);
				if (isNaN(date)) return "";
				const localDate = new Date(
					date.getTime() + date.getTimezoneOffset() * 60000,
				);
				return localDate.toLocaleString("en-GB", {
					year: "numeric",
					month: "short",
					day: "numeric",
					hour: "2-digit",
					minute: "2-digit",
				});
			} catch (error) {
				console.error("Error formatting start date:", error);
				return "";
			}
		},

		formattedEndDateTime() {
			try {
				const endDateStr = `${this.event.end_date.split("T")[0]}T${this.event.end_time
					}:00Z`;
				const date = new Date(endDateStr);
				if (isNaN(date)) return "";
				const localDate = new Date(
					date.getTime() + date.getTimezoneOffset() * 60000,
				);
				return localDate.toLocaleString("en-GB", {
					year: "numeric",
					month: "short",
					day: "numeric",
					hour: "2-digit",
					minute: "2-digit",
				});
			} catch (error) {
				console.error("Error formatting end date:", error);
				return "";
			}
		},

		price() {
			if (this.event.Price > 0) return `$${this.event.Price.toFixed(2)}`;
			return "Free";
		},

		handleNavigation() {
			return () => {
				window.open(
					`https://${this.event.organizer[0].handle}.trekko.xyz/`,
					"_blank",
				);
			};
		},

		eventStatus() {
			const now = new Date();
			try {
				const startDateStr = `${this.event.date.split("T")[0]}T${this.event.start_time
					}:00Z`;
				const endDateStr = `${this.event.end_date.split("T")[0]}T${this.event.end_time
					}:00Z`;
				const eventStartDate = new Date(startDateStr);
				const eventEndDate = new Date(endDateStr);
				if (isNaN(eventStartDate.getTime()) || isNaN(eventEndDate.getTime())) {
					return { status: "Invalid Date", color: "bg-gray-500" };
				}
				const localStart = new Date(
					eventStartDate.getTime() + eventStartDate.getTimezoneOffset() * 60000,
				);
				const localEnd = new Date(
					eventEndDate.getTime() + eventEndDate.getTimezoneOffset() * 60000,
				);
				if (now < localStart) {
					return (localStart - now) / (1000 * 60 * 60) > 24
						? { status: "RSVP", color: "bg-blue-500" }
						: { status: "STARTING SOON", color: "bg-yellow-500" };
				} else if (now >= localStart && now <= localEnd) {
					return { status: "LIVE", color: "bg-green-500" };
				} else {
					return { status: "ENDED", color: "bg-red-500" };
				}
			} catch (error) {
				console.error("Error calculating event status:", error);
			}
			return { status: "Status Unknown", color: "bg-gray-500" };
		},
	},

	methods: {
		// ─────────────────────────────────────────────
		// SEO — single source of truth
		// ─────────────────────────────────────────────
		setEventHead() {
			if (!this.event) return;

			const title = `${this.event.title} — Trekko`;
			const rawDesc =
				this.event.description ||
				"Discover and book exciting events with Trekko!";
			const description =
				rawDesc.replace(/<[^>]*>/g, "").slice(0, 155) +
				(rawDesc.length > 155 ? "…" : "");
			const image = this.event.image_url || "https://trekko.xyz/favicon.png";
			const url = window.location.href;
			const keywords = this.event.categories?.length
				? this.event.categories.map((c) => c.Name || c.name || c).join(", ")
				: "events, tickets, entertainment, Trekko";

			useHead({
				title,
				meta: [
					// ── Core SEO ──────────────────────────────
					{ name: "description", content: description },
					{ name: "keywords", content: keywords },
					{ name: "robots", content: "index, follow" },

					// ── Open Graph ────────────────────────────
					{ property: "og:type", content: "website" },
					{ property: "og:site_name", content: "Trekko" },
					{ property: "og:title", content: title },
					{ property: "og:description", content: description },
					{ property: "og:image", content: image },
					{ property: "og:url", content: url },

					// ── Twitter Card ──────────────────────────
					{ name: "twitter:card", content: "summary_large_image" },
					{ name: "twitter:site", content: "@gotrekko" },
					{ name: "twitter:title", content: title },
					{ name: "twitter:description", content: description },
					{ name: "twitter:image", content: image },
					{
						name: "twitter:image:alt",
						content: `${this.event.title} event cover`,
					},
				],
				link: [{ rel: "canonical", href: url }],
			});
		},

		// ─────────────────────────────────────────────
		// FETCH EVENT
		// ─────────────────────────────────────────────
		async fetchEventDetails() {
			try {
				const response = await apiService.getEventDetails(
					this.$route.params.id,
				);
				if (response && response.data.data) {
					this.event = response.data.data;
					this.isEventBookable = response.data.data.bookable;
					this.enableRecommendMusicSection =
						response.data.data.music_recommendations;

					if (Array.isArray(this.event.organizer_logos)) {
						this.event.organizerLogos = this.event.organizer_logos.map(
							(logo) => ({
								url:
									logo.LogoURL ||
									"https://www.adaptivewfs.com/wp-content/uploads/2020/07/logo-placeholder-image.png",
							}),
						);
					} else {
						this.event.organizerLogos = [];
					}

					if (
						Array.isArray(this.event.tickets) &&
						this.event.tickets.length > 0
					) {
						this.event.tickets = this.event.tickets.map((ticket) => ({
							id: ticket.ID,
							name: ticket.Type || "Unknown Ticket",
							price: parseFloat(ticket.Amount) || 0,
						}));
					}

					if (this.event.categories?.length > 0) {
						this.event.categories = this.event.categories.map((category) => ({
							id: category.ID,
							name: category.Name || "Unknown Category",
							description: category.Description || "No description provided",
						}));
					}

					// if (this.event.latitude && this.event.longitude) {
					// 	this.$nextTick(() => {
					// 		this.initializeMap(this.event.latitude, this.event.longitude);
					// 	});
					// }

					// ── Set SEO after data is ready ──
					this.setEventHead();
				} else {
					this.event = null;
				}
			} catch (error) {
				this.event = null;
			} finally {
				this.loading = false;
			}
		},

		// ─────────────────────────────────────────────
		// MAP
		// ─────────────────────────────────────────────
		initializeMap(lat, lng) {
			const cartoKey = process.env.VUE_APP_CARTON_KEY;
			console.log("CARTO API Key:",cartoKey ? "Loaded" : "Missing");
			const mapElement = document.getElementById("event-map");
			if (!mapElement) return;
			if (this.map) this.map.remove();

			this.map = L.map(mapElement, {
				zoomControl: false,
				attributionControl: true,
			}).setView([lat, lng], 14);

			const tileUrl = this.isLight
				? "https://basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png?key=" + cartoKey
				: "https://basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png?key=" + cartoKey;

			L.tileLayer(tileUrl, { subdomains: "abcd", maxZoom: 19 }).addTo(this.map);

			const imageUrl = this.event.image_url || "";
			const svgMarker = `
				<svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
					<defs>
						<clipPath id="clip-circle"><circle cx="20" cy="20" r="17"/></clipPath>
						<filter id="pin-shadow" x="-40%" y="-40%" width="180%" height="180%">
							<feDropShadow dx="0" dy="3" stdDeviation="4" flood-color="#1a9b8a" flood-opacity="0.45"/>
						</filter>
					</defs>
					<circle cx="20" cy="20" r="20" fill="#1a9b8a" filter="url(#pin-shadow)"/>
					<circle cx="20" cy="20" r="17" fill="#0d6b5e"/>
					${imageUrl
					? `<image href="${imageUrl}" x="3" y="3" width="34" height="34" clip-path="url(#clip-circle)" preserveAspectRatio="xMidYMid slice"/>`
					: `<circle cx="20" cy="20" r="6" fill="white"/>`
				}
					<circle cx="20" cy="20" r="17" fill="none" stroke="white" stroke-width="2"/>
				</svg>`;

			const icon = L.divIcon({
				html: svgMarker,
				className: "",
				iconSize: [40, 40],
				iconAnchor: [20, 20],
				popupAnchor: [0, -24],
			});
			const marker = L.marker([lat, lng], { icon }).addTo(this.map);

			const directionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}&travelmode=driving`;
			const bg = this.isLight ? "#ffffff" : "#132420";
			const textColor = this.isLight ? "#0f1f1c" : "#ffffff";
			const subColor = this.isLight ? "#0d6b5e" : "#a8d5cc";

			marker
				.bindPopup(
					`<div style="font-family:Inter,sans-serif;background:${bg};border-radius:16px;padding:12px;min-width:150px;max-width:200px;text-align:center;">
					${imageUrl
						? `<img src="${imageUrl}" style="width:64px;height:64px;border-radius:50%;object-fit:cover;margin:0 auto 8px;display:block;border:2.5px solid #1a9b8a;"/>`
						: `<div style="width:64px;height:64px;border-radius:50%;background:#1a9b8a22;margin:0 auto 8px;display:flex;align-items:center;justify-content:center;border:2.5px solid #1a9b8a;"><svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="#1a9b8a"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/></svg></div>`
					}
					<p style="font-size:12px;font-weight:700;color:${textColor};margin:0 0 4px;line-height:1.3;">${this.event.title
					}</p>
					<p style="font-size:10px;color:${subColor};margin:0 0 10px;">${this.address || ""
					}</p>
					<a href="${directionsUrl}" target="_blank"
						style="display:inline-flex;align-items:center;gap:5px;background:#1a9b8a;color:#fff;font-size:10px;font-weight:700;padding:6px 12px;border-radius:20px;text-decoration:none;letter-spacing:.04em;">
						${this.$t("eventDetail.getDirections")}
					</a>
				</div>`,
					{ className: "trekko-popup", maxWidth: 220 },
				)
				.openPopup();

			fetch(
				`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`,
			)
				.then((r) => r.json())
				.then((data) => {
					this.address = data.display_name;
				})
				.catch((err) => {
					console.error("Geocoding error:", err);
				});
		},

		// ─────────────────────────────────────────────
		// TICKET / BOOKING
		// ─────────────────────────────────────────────
		selectTicket(ticket) {
			this.selectedTicketId = ticket.id;
			this.selectedTicket = ticket.id;
			this.ticketQty = 1;
		},

		formatCurrency(value) {
			return new Intl.NumberFormat("en-US", {
				style: "currency",
				currency: "USD",
			}).format(value);
		},

		openBookingModal() {
			if (this.selectedTicketId) {
				this.isBookingModalOpen = true;
			} else {
				this.toast.warning("You must choose a ticket");
			}
		},

		openRecommendMusicModal() {
			this.isRecommendMusicModalOpen = true;
		},

		closeModal() {
			this.isRecommendMusicModalOpen = false;
			this.isBookingModalOpen = false;
			this.selectedTicketId = null;
			this.selectedTicket = null;
			this.ticketQty = 1;
		},

		// ─────────────────────────────────────────────
		// SHARE
		// ─────────────────────────────────────────────
		async shareEvent() {
			if (navigator.share) {
				try {
					await navigator.share({
						title: this.event?.title,
						url: window.location.href,
					});
				} catch (err) {
					if (err.name !== "AbortError") console.error("Share failed:", err);
				}
			} else {
				await navigator.clipboard.writeText(window.location.href);
				this.toast.success(this.$t("eventDetail.shareEvent"));
			}
		},
	},

	watch: {
		isLight() {
        if (this.event?.latitude && this.event?.longitude) {
            this.$nextTick(() => {
                this.initializeMap(
                    this.event.latitude,
                    this.event.longitude
                );
            });
        }
    },
		event(newEvent) {
			if (newEvent) {
				// Re-init map if coordinates present
				if (newEvent.latitude && newEvent.longitude) {
					this.$nextTick(() => {
						this.initializeMap(newEvent.latitude, newEvent.longitude);
					});
				}
				// Keep SEO in sync with event data
				this.setEventHead();
			}
		},
	},

	created() {
		this.fetchEventDetails();
	},
};
</script>

<style>
* {
	min-width: 0;
}

@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800&family=Inter:wght@300;400;500&display=swap");

p,
h1,
h2,
h3,
h4,
h5,
h6,
span,
a {
	word-break: break-word;
}

.trekko-popup {
	max-width: calc(100vw - 40px) !important;
}

.trekko-popup .leaflet-popup-content-wrapper {
	background: transparent !important;
	border: none !important;
	box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3) !important;
	border-radius: 16px !important;
	padding: 0 !important;
}

.trekko-popup .leaflet-popup-content {
	margin: 0 !important;
}

.trekko-popup .leaflet-popup-tip-container {
	display: none !important;
}

.scrollbar-none::-webkit-scrollbar {
	display: none;
}

.scrollbar-none {
	-ms-overflow-style: none;
	scrollbar-width: none;
}

.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.2s ease;
}

.fade-enter-from,
.fade-leave-to {
	opacity: 0;
}

html,
body {
	overflow-x: hidden;
	max-width: 100%;
}

*,
*::before,
*::after {
	box-sizing: border-box;
	min-width: 0;
}

img,
svg,
video,
canvas {
	max-width: 100%;
	height: auto;
}

.leaflet-container {
	max-width: 100%;
}
</style>
